
import {Fragment, useEffect, useState} from 'react';
import {
  BottomNavigation,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  Paper,
  TextField,
  Slider,
  Typography,
} from '@mui/material';
import {useStyles} from '../campaign-management/styles';
import {Label} from '@app/components/elements/Label';
import {
  CheckBoxOutlineBlank,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {useDispatch, useSelector} from 'react-redux';
import { filterSelector } from '../redux';
import { fetchApprovalDataCreator, approvalStateActions } from '@app/screens/campaign-management/pages/approval/redux/slice';
import { approvalStateSelector } from '@app/screens/campaign-management/pages/approval/redux/selectors';

// Transform API response to component filter format
const transformApiDataToFilters = (apiData: any) => {
  // If API response has 'filters' wrapper, extract it
  if (apiData.filters) {
    // Data is already in component format inside 'filters' key
    const transformedData = JSON.parse(JSON.stringify(apiData.filters));
    
    // Ensure Admin's pending is selected by default
    if (transformedData['Approval Status'] && transformedData['Approval Status'].filterData) {
      transformedData['Approval Status'].filterData.forEach((category: any) => {
        if (category.id === 'admin' && category.filterData) {
          category.filterData.forEach((item: any) => {
            if (item.id === 'admin_pending') {
              item.isChecked = true;
            }
          });
        }
      });
    }
    
    // Range values are already in the filters object from API
    // No need to set defaults, they come from API
    return {
      filters: transformedData
    };
  }
  
  // If the data is already in component format (has keys like 'Approval Status'), return it directly
  if (apiData['Approval Status']) {
    // Ensure Admin's pending is selected by default
    const transformedData = JSON.parse(JSON.stringify(apiData));
    if (transformedData['Approval Status'] && transformedData['Approval Status'].filterData) {
      transformedData['Approval Status'].filterData.forEach((category: any) => {
        if (category.id === 'admin' && category.filterData) {
          category.filterData.forEach((item: any) => {
            if (item.id === 'admin_pending') {
              item.isChecked = true;
            }
          });
        }
      });
    }
    
    return {
      filters: transformedData
    };
  }

  // Otherwise, transform API format to component format
  const transformedData: any = {};

  Object.entries(apiData).forEach(([key, value]: [string, any]) => {
    if (key === 'approvalStatus') {
      transformedData['Approval Status'] = {
        isChecked: false,
        collapsed: false,
        filterData: [
          {
            id: 'admin',
            value: 'Admin',
            isChecked: false,
            isSubCategory: true,
            filterData: [
              { id: 'admin_approved', value: 'Approved', isChecked: false },
              { id: 'admin_pending', value: 'Pending', isChecked: true },
              { id: 'admin_rejected', value: 'Rejected', isChecked: false },
            ]
          },
          {
            id: 'flm',
            value: 'FLM',
            isChecked: false,
            isSubCategory: true,
            filterData: [
              { id: 'flm_approved', value: 'Approved', isChecked: false },
              { id: 'flm_pending', value: 'Pending', isChecked: false },
              { id: 'flm_rejected', value: 'Rejected', isChecked: false },
            ]
          },
          {
            id: 'slm',
            value: 'SLM',
            isChecked: false,
            isSubCategory: true,
            filterData: [
              { id: 'slm_approved', value: 'Approved', isChecked: false },
              { id: 'slm_pending', value: 'Pending', isChecked: false },
              { id: 'slm_rejected', value: 'Rejected', isChecked: false },
            ]
          },
        ]
      };
    } else {
      const filterKey = key === 'completionStatus' ? 'Completion Status' :
                       key === 'countries' ? 'Country' :
                       key === 'states' ? 'State' :
                       key === 'hQs' ? 'HQ' :
                       key === 'divisions' ? 'Division' : key;

      if (Array.isArray(value)) {
        transformedData[filterKey] = {
          isChecked: false,
          collapsed: true,
          filterData: value.map((item: any) => {
            let id, displayValue;
            if (item.completionStatusId !== undefined) {
              id = item.completionStatusId;
              displayValue = item.completionStatusName;
            } else if (item.countryId !== undefined) {
              id = item.countryId;
              displayValue = item.countryName;
            } else if (item.stateId !== undefined) {
              id = item.stateId;
              displayValue = item.stateName;
            } else if (item.headQuaterId !== undefined) {
              id = item.headQuaterId;
              displayValue = item.headQuaterName;
            } else if (item.divisionId !== undefined) {
              id = item.divisionId;
              displayValue = item.divisionName;
            } else {
              id = item.id || item.value;
              displayValue = item.value || item.name;
            }

            return {
              id: id,
              value: displayValue,
              isChecked: false
            };
          })
        };
      }
    }
  });

  // Embed range values directly in filters object
  transformedData.maxDm = apiData.maxDM || 1000000000;
  transformedData.minDm = apiData.minDM || 0;
  transformedData.maxDms = apiData.maxDMS || 100000000;
  transformedData.minDms = apiData.minDMS || 0;
  transformedData.maxSOW = apiData.maxSOW || 1000;
  transformedData.minSOW = apiData.minSOW || 0;

  return {
    filters: transformedData
  };
};

const IndeterminateIcon = () => {
  const classes = useStyles();

  return (
    <>
      <CheckBoxOutlineBlank />
      <div className={classes.indeterminateBox} />
    </>
  );
};

const ShowListFilters = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const filtersDataFromApi = useSelector(filterSelector.getFilterState());
  const showListPayloadData = useSelector(approvalStateSelector.getApprovalStatusData());
  const approvalData = useSelector(approvalStateSelector.getApprovalData());
  
  // Check if API data is available
  const hasApiData = filtersDataFromApi && Object.keys(filtersDataFromApi).length > 0;
  
  // Store original API data for reset functionality
  const [originalApiData, setOriginalApiData] = useState<any>(null);
  
  // Initialize state - will be populated when API data arrives
  const [filters, setFiltersState] = useState<any>(null);
  const [dmRange, setDmRange] = useState<number[]>([0, 0]);
  const [dmsRange, setDmsRange] = useState<number[]>([0, 0]);
  const [sowRange, setSowRange] = useState<number[]>([0, 0]);

  // Transform and populate state when API data arrives (only on FIRST load)
  useEffect(() => {
    if (hasApiData && !originalApiData) {
      // Store original API data for reset (only first time)
      setOriginalApiData(filtersDataFromApi);
      
      const transformedData = transformApiDataToFilters(filtersDataFromApi);
      setFiltersState(transformedData.filters);
      setDmRange([transformedData.filters.minDm || 0, transformedData.filters.maxDm || 0]);
      setDmsRange([transformedData.filters.minDms || 0, transformedData.filters.maxDms || 0]);
      setSowRange([transformedData.filters.minSOW || 0, transformedData.filters.maxSOW || 0]);
    }
  }, [hasApiData, filtersDataFromApi, originalApiData]);

  const handleParentListItemClick = (parentKeyName: string) => {
    let updatedFilters = JSON.parse(JSON.stringify(filters ? filters : {}));
    // Only update collapsed property if the item is a filter object (not a number like range values)
    if (updatedFilters[parentKeyName] && typeof updatedFilters[parentKeyName] === 'object' && 'collapsed' in updatedFilters[parentKeyName]) {
      updatedFilters[parentKeyName].collapsed = !updatedFilters[parentKeyName].collapsed;
      setFiltersState(updatedFilters);
    }
  };

  const handleParentCheckbox = (parentKeyName: string, filter: any) => {
    let filtersCopy = JSON.parse(JSON.stringify(filters ? filters : {}));
    if (filtersCopy && filtersCopy[parentKeyName]) {
      const newCheckedState = !filter?.isChecked;
      filtersCopy[parentKeyName].isChecked = newCheckedState;
      
      if (filtersCopy[parentKeyName].filterData) {
        filtersCopy[parentKeyName].filterData.forEach((item: any) => {
          if (item.isSubCategory && item.filterData) {
            // Handle sub-categories like FLM, SLM, Admin
            item.isChecked = newCheckedState;
            item.filterData.forEach((subItem: any) => {
              subItem.isChecked = newCheckedState;
            });
          } else {
            // Handle regular items
            item.isChecked = newCheckedState;
          }
        });
      }
      setFiltersState(filtersCopy);
    }
  };

  const handleChildCheckbox = (parentKeyName: string, childKey: any) => {
    let filtersCopy = JSON.parse(JSON.stringify(filters ? filters : {}));

    if (filtersCopy[parentKeyName] && filtersCopy[parentKeyName]?.filterData) {
      filtersCopy[parentKeyName].filterData = filtersCopy[parentKeyName].filterData?.map((optionKey: any) => {
        if (optionKey.id === childKey.id) {
          return {
            ...optionKey,
            isChecked: !childKey?.isChecked,
          };
        }
        return optionKey;
      });

      const uncheckedChild = filtersCopy[parentKeyName]?.filterData?.find(
        (child: any) => !child?.isChecked,
      );
      filtersCopy[parentKeyName]['isChecked'] = !Boolean(uncheckedChild);
    }

    setFiltersState(filtersCopy);
  };

  const handleSubCategoryCheckbox = (parentKeyName: string, subCategory: any) => {
    let filtersCopy = JSON.parse(JSON.stringify(filters ? filters : {}));
    
    if (filtersCopy[parentKeyName] && filtersCopy[parentKeyName].filterData) {
      filtersCopy[parentKeyName].filterData = filtersCopy[parentKeyName].filterData.map((category: any) => {
        if (category.id === subCategory.id) {
          const newCheckedState = !subCategory.isChecked;
          return {
            ...category,
            isChecked: newCheckedState,
            filterData: category.filterData?.map((subItem: any) => ({
              ...subItem,
              isChecked: newCheckedState,
            }))
          };
        }
        return category;
      });
      
      // Update parent checked state
      const uncheckedCategory = filtersCopy[parentKeyName].filterData.find(
        (category: any) => !category.isChecked
      );
      filtersCopy[parentKeyName].isChecked = !Boolean(uncheckedCategory);
    }
    
    setFiltersState(filtersCopy);
  };

  const handleSubItemCheckbox = (parentKeyName: string, subCategoryId: string, subItem: any) => {
    let filtersCopy = JSON.parse(JSON.stringify(filters ? filters : {}));
    
    if (filtersCopy[parentKeyName] && filtersCopy[parentKeyName].filterData) {
      filtersCopy[parentKeyName].filterData = filtersCopy[parentKeyName].filterData.map((category: any) => {
        if (category.id === subCategoryId) {
          const updatedSubItems = category.filterData.map((item: any) => {
            if (item.id === subItem.id) {
              return { ...item, isChecked: !subItem.isChecked };
            }
            return item;
          });
          
          // Update sub-category checked state
          const uncheckedSubItem = updatedSubItems.find((item: any) => !item.isChecked);
          return {
            ...category,
            isChecked: !Boolean(uncheckedSubItem),
            filterData: updatedSubItems
          };
        }
        return category;
      });
      
      // Update parent checked state
      const uncheckedCategory = filtersCopy[parentKeyName].filterData.find(
        (category: any) => !category.isChecked
      );
      filtersCopy[parentKeyName].isChecked = !Boolean(uncheckedCategory);
    }
    
    setFiltersState(filtersCopy);
  };

  const checkIfIndeterminate = (filter: any, parentKeyName?: string) => {
    if (filter.isChecked) return false;
    
    if (parentKeyName === 'Approval Status') {
      // For Approval Status, check if any sub-category has selected items
      const hasSelectedItems = filter.filterData?.some((category: any) => {
        if (category.isSubCategory) {
          return category.filterData?.some((item: any) => item.isChecked);
        }
        return category.isChecked;
      });
      return hasSelectedItems;
    }
    
    let isIndeterminate = filter?.filterData?.find((item: any) => item.isChecked);
    return Boolean(isIndeterminate);
  };

  const checkSubCategoryIndeterminate = (subCategory: any) => {
    if (subCategory.isChecked) return false;
    return subCategory.filterData?.some((item: any) => item.isChecked);
  };

  const clearAllFilter = () => {
    // Reset filters to initial API state (all unchecked except Admin Pending)
    if (originalApiData) {
      const transformedData = transformApiDataToFilters(originalApiData);
      setFiltersState(transformedData.filters);
      setDmRange([transformedData.filters.minDm || 0, transformedData.filters.maxDm || 100000000]);
      setDmsRange([transformedData.filters.minDms || 0, transformedData.filters.maxDms || 10000000]);
      setSowRange([transformedData.filters.minSOW || 0, transformedData.filters.maxSOW || 100]);
    }
  };

  const applyFilters = () => {
    // Initialize filters in the required API format
    const appliedFilters: any = {
      approvalStatus: [] as any[],
      completionStatus: [] as any[],
      countries: [] as any[],
      states: [] as any[],
      hQs: [] as any[],
      divisions: [] as any[],
      minDMS: dmsRange[0],
      maxDMS: dmsRange[1],
      minDM: dmRange[0],
      maxDM: dmRange[1],
      minSOW: sowRange[0],
      maxSOW: sowRange[1],
      pageNo: 1,
      pageSize: 10,
      engagementId: showListPayloadData?.engagementUser?.campaignIdForPayload,
      staffPositionId: showListPayloadData?.engagementUser?.staffPositionId
    };
    
    // Process filters
    Object.entries(filters).forEach(([key, value]: [string, any]) => {
      if (key === 'Approval Status') {
        // Collect all selected approval status IDs
        value.filterData?.forEach((category: any) => {
          if (category.isSubCategory) {
            const selectedItems = category.filterData?.filter((item: any) => item.isChecked);
            selectedItems?.forEach((item: any) => {
              // Map approval status to numeric IDs (customize as needed)
              let approvalStatusId = 0;
              if (item.id === 'admin_approved') approvalStatusId = 1;
              else if (item.id === 'admin_pending') approvalStatusId = 2;
              else if (item.id === 'admin_rejected') approvalStatusId = 3;
              else if (item.id === 'flm_approved') approvalStatusId = 4;
              else if (item.id === 'flm_pending') approvalStatusId = 5;
              else if (item.id === 'flm_rejected') approvalStatusId = 6;
              else if (item.id === 'slm_approved') approvalStatusId = 7;
              else if (item.id === 'slm_pending') approvalStatusId = 8;
              else if (item.id === 'slm_rejected') approvalStatusId = 9;
              
              appliedFilters.approvalStatus.push({
                approvalStatusId: approvalStatusId
              });
            });
          }
        });
      } else if (key === 'Completion Status') {
        const selectedItems = value.filterData?.filter((item: any) => item.isChecked) || [];
        appliedFilters.completionStatus = selectedItems.map((item: any) => ({
          completionStatusId: item.id,
          completionStatusName: item.value
        }));
      } else if (key === 'Country') {
        const selectedItems = value.filterData?.filter((item: any) => item.isChecked) || [];
        appliedFilters.countries = selectedItems.map((item: any) => ({
          countryId: item.id,
          countryName: item.value
        }));
      } else if (key === 'State') {
        const selectedItems = value.filterData?.filter((item: any) => item.isChecked) || [];
        appliedFilters.states = selectedItems.map((item: any) => ({
          stateId: item.id,
          stateName: item.value
        }));
      } else if (key === 'HQ') {
        const selectedItems = value.filterData?.filter((item: any) => item.isChecked) || [];
        appliedFilters.hQs = selectedItems.map((item: any) => ({
          headQuaterId: item.id,
          headQuaterName: item.value
        }));
      } else if (key === 'Division') {
        const selectedItems = value.filterData?.filter((item: any) => item.isChecked) || [];
        appliedFilters.divisions = selectedItems.map((item: any) => ({
          divisionId: item.id,
          divisionName: item.value
        }));
      }
    });    
    // Dispatch the same action that ViewList uses on mount, triggering data refetch
        dispatch(approvalStateActions.setLoaderData({ isLoading: true }));
        dispatch(
          fetchApprovalDataCreator({
            url: 'approvals/showlist',
            engagementId: showListPayloadData?.engagementUser?.campaignIdForPayload,
            staffpositionId: showListPayloadData?.engagementUser?.staffPositionId,
            filters: appliedFilters
          }),
        );

        // Update payloads with current filter state
        dispatch(
          approvalStateActions.setApprovalData({
            showListPayloadData: {
              url: 'approvals/showlist',
              engagementId: showListPayloadData?.engagementUser?.campaignIdForPayload,
              staffpositionId: showListPayloadData?.engagementUser?.staffPositionId,
              filters: appliedFilters
            },
          }),
        )
        dispatch(approvalStateActions.setApprovalData({
          approvalHistoryPayloadData: {
            url: 'approvals/history',
            engagementId: showListPayloadData?.engagementUser?.campaignIdForPayload,
            staffpositionId: showListPayloadData?.engagementUser?.staffPositionId
          }
        }))
    return appliedFilters;
  };

  const getExpandIcon = (collapsed: boolean) => {
    if (collapsed) return <ExpandMore />;
    else return <ExpandLess />;
  };

  const listItemText = (text: string) => (
    <Label title={text} classes={classes.listItemText} />
  );

  const parentListItemText = (text: string) => (
    <Label title={text} classes={classes.parentListItemText} />
  );

  const renderRangeSlider = (
    title: string,
    range: number[],
    setRange: (range: number[]) => void,
    min: number,
    max: number,
    step?: number
  ) => {
    const formatValue = (value: number) => {
      if (value >= 10000000) {
        return `${(value / 10000000).toFixed(1)} Cr`;
      } else if (value >= 100000) {
        return `${(value / 100000).toFixed(1)} L`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}K`;
      }
      return value.toString();
    };

    return (
      <Box marginBottom={'15px'} marginLeft={'10px'}>
        <ListItem disablePadding>
          <ListItemIcon>
            <Checkbox className={classes.checkbox} checked={false} disabled />
          </ListItemIcon>
          {parentListItemText(title)}
          <Box paddingRight={'10px'}>
            <ExpandMore />
          </Box>
        </ListItem>
        <Box paddingLeft={'40px'} paddingRight={'20px'}>
          <Typography variant="body2" color="textSecondary">
            Range: {formatValue(range[0])} - {formatValue(range[1])}
          </Typography>
          <Slider
            value={range}
            onChange={(_, newValue) => setRange(newValue as number[])}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => value.toLocaleString()}
            min={min}
            max={max}
            step={step || undefined}
            marks={step ? true : false}
            size="small"
          />
        </Box>
      </Box>
    );
  };

  const renderApprovalStatusItems = (filter: any, parentKeyName: string) => {
    return filter.filterData?.map((category: any) => (
      <Fragment key={category.id}>
        {/* Sub-category (FLM, SLM, Admin) */}
        <ListItem
          disablePadding
          key={`sub_${category.id}`}
          data-testid={`list_item_${category.id}`}
          sx={{ paddingLeft: 2 }}
        >
          <ListItemIcon>
            <Checkbox
              className={classes.checkbox}
              checked={category?.isChecked || false}
              indeterminate={checkSubCategoryIndeterminate(category)}
              indeterminateIcon={<IndeterminateIcon />}
              onClick={() => handleSubCategoryCheckbox(parentKeyName, category)}
              data-testid={`subcategory_${category.id}`}
            />
          </ListItemIcon>
          <Typography variant="body1" fontWeight="bold" color="primary">
            {category.value}
          </Typography>
        </ListItem>

        {/* Sub-category items (Approved, Pending, Rejected) */}
        {category.filterData?.map((subItem: any) => (
          <ListItem
            disablePadding
            key={`${category.id}_${subItem.id}`}
            data-testid={`list_item_${subItem.id}`}
            sx={{ paddingLeft: 4 }}
          >
            <ListItemIcon>
              <Checkbox
                className={classes.checkbox}
                checked={subItem?.isChecked || false}
                onClick={() => handleSubItemCheckbox(parentKeyName, category.id, subItem)}
                data-testid={`subitem_${category.id}_${subItem.id}`}
              />
            </ListItemIcon>
            {listItemText(subItem.value)}
          </ListItem>
        ))}
      </Fragment>
    ));
  };

  const renderRegularItems = (filter: any, parentKeyName: string) => {
    return filter.filterData?.map((childKey: any) => (
      <ListItem
        disablePadding
        key={`${parentKeyName}_${childKey?.id}`}
        data-testid={`list_item_${childKey?.id}`}
      >
        <ListItemIcon>
          <Checkbox
            className={classes.checkbox}
            checked={childKey?.isChecked || false}
            disabled={childKey?.isDisable || false}
            onClick={() => handleChildCheckbox(parentKeyName, childKey)}
            data-testid={`option_${parentKeyName}_${childKey?.id}`}
          />
        </ListItemIcon>
        {listItemText(childKey?.value)}
      </ListItem>
    ));
  };

  const secondaryListItems = (filter: any, parentKeyName: string) => {
    return (
      <Fragment key={parentKeyName}>
        {filter ? (
          <Box marginBottom={'15px'} marginLeft={'10px'}>
            <ListItem
              disablePadding
              button
              key={parentKeyName}
              onClick={() => handleParentListItemClick(parentKeyName)}
              data-testid={`list_item_${parentKeyName}`}
            >
              <ListItemIcon>
                <Checkbox
                  className={classes.checkbox}
                  checked={filter?.isChecked || false}
                  indeterminate={checkIfIndeterminate(filter, parentKeyName)}
                  disabled={filter?.isDisable || false}
                  indeterminateIcon={<IndeterminateIcon />}
                  onClick={event => {
                    event.stopPropagation();
                    handleParentCheckbox(parentKeyName, filter);
                  }}
                  data-testid={`parent_${parentKeyName}`}
                />
              </ListItemIcon>
              {parentListItemText(parentKeyName)}
              <Box paddingRight={'10px'}>
                {getExpandIcon(filter?.collapsed)}
              </Box>
            </ListItem>

            <Collapse in={!filter?.collapsed} timeout="auto" unmountOnExit>
              <List
                component="div"
                disablePadding
                className={classes.marginLeft15}
              >
                {parentKeyName === 'Approval Status'
                  ? renderApprovalStatusItems(filter, parentKeyName)
                  : renderRegularItems(filter, parentKeyName)}
              </List>
            </Collapse>
          </Box>
        ) : (
          <></>
        )}
      </Fragment>
    );
  };

  return (
    <Box
      margin={'20px'}
      marginTop={'10px'}
      borderRadius={'20px'}
      className={`${classes.backgroundWhite} hide-scrollbar`}
      height={'100vh'}
      overflow={'scroll'}
    >
      {filters && Object.keys(filters)?.length > 0 ? (
        <>
          <Box
            display={'flex'}
            justifyContent="space-between"
            marginBottom={'10px'}
            padding={'10px'}
          >
            <Chip label="Filter" size="small" className={classes.chipFilter} />
            <Chip
              label="Clear All"
              size="small"
              clickable
              onClick={clearAllFilter}
              className={classes.chipClearAll}
              data-testid="clear-filters"
            />
          </Box>

          <Box paddingBottom={'100px'}>
            {/* Render standard filters (exclude range values) */}
            {Object.keys(filters)
              ?.filter((key) => !['maxDm', 'minDm', 'maxDms', 'minDms', 'maxSOW', 'minSOW'].includes(key))
              ?.map((key) => secondaryListItems(filters[key], key))
            }
            
            {/* Render range sliders */}
            {renderRangeSlider('DM', dmRange, setDmRange, 0, 1000000000, 100000000)}
            {renderRangeSlider('DMS', dmsRange, setDmsRange, 0, 100000000, 10000000)}
            {renderRangeSlider('SOW', sowRange, setSowRange, 0, 1000, 10)}
          </Box>

          <Paper className={`${classes.paper} height-100`} elevation={3}>
            <BottomNavigation showLabels value={0}>
              <Box className={classes.applyBox}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  className={classes.paperButton}
                  data-testid={'apply_filters'}
                >
                  Apply
                </Button>
              </Box>
            </BottomNavigation>
          </Paper>
        </>
      ) : (
        <Box sx={{display: 'flex', justifyContent: 'center', mt: 2}}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Box>
  );
};

export default ShowListFilters;